import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Box,
  createStyles,
  Loader,
  Menu,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {useSkillOverflowMenuHandlers} from 'llm/chat-library/skills/SkillOverflowMenuContext';
import VersionHistoryOAuthClientAttribution from 'notebook/version-history/VersionHistoryOAuthClientAttribution';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import {
  AGENT_SKILL_VERSIONS_QUERY,
  AgentSkillVersionsQueryData,
  downloadSkillBundle,
  revertSkillToVersion,
  SkillSummary,
  SkillVersion,
  AgentSkillVersionsQueryResult,
} from './SkillsDAO';

interface SkillVersionHistoryPanelProps {
  skill: SkillSummary;
  onClose: () => void;
  onReverted: () => void;
  onVersionSelected: (version: SkillVersion | null) => void;
  selectedVersionId: string | null;
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.sm,
  },
  headerContainer: {
    margin: theme.spacing.lg,
    marginBottom: theme.spacing.xl,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.gray[9],
  },
  versionsList: {
    flex: 1,
    overflowY: 'auto',
  },
  row: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  rowSelected: {
    backgroundColor: theme.colors.blue[0],
    '&:hover': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: `${theme.spacing.sm} calc(${theme.spacing.md} + ${theme.spacing.xl}) ${theme.spacing.sm} ${theme.spacing.sm}`,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  rowAction: {
    position: 'absolute',
    right: theme.spacing.md,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  timestamp: {
    fontSize: theme.fontSizes.md,
    lineHeight: theme.spacing.lg,
    color: theme.colors.gray[9],
  },
  timestampSelected: {
    fontWeight: 600,
  },
  avatarRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing.xs,
  },
  userName: {
    fontSize: theme.fontSizes.sm,
    lineHeight: theme.spacing.lg,
  },
}));

function SkillVersionHistoryRow({
  version,
  isSelected,
  onClick,
  onDownload,
  onRevert,
  isReverting,
  withinPortal,
}: {
  version: SkillVersion;
  isSelected: boolean;
  onClick: () => void;
  onDownload: (() => void) | undefined;
  onRevert: (() => void) | undefined;
  isReverting: boolean;
  withinPortal: boolean;
}): JSX.Element {
  const {classes, cx} = useStyles();

  const avatarEntity: HasAvatar | null = version.creatorName ? {name: version.creatorName} : null;

  return (
    <div className={cx(classes.row, {[classes.rowSelected]: isSelected})}>
      <UnstyledButton
        className={classes.rowContent}
        onClick={onClick}
        {...getTestProps({component: 'SkillVersionHistoryRow', element: 'root'})}
      >
        <span
          className={cx(classes.timestamp, {[classes.timestampSelected]: isSelected})}
          {...getTestProps({component: 'SkillVersionHistoryRow', element: 'timestamp'})}
        >
          {readableTime(version.createdAt)}
        </span>
        {avatarEntity && (
          <div className={classes.avatarRow}>
            <Avatar
              size={AvatarSize.XXSMALL}
              shape={AvatarShape.CIRCLE}
              entity={avatarEntity}
              showTooltip={false}
            />
            <span className={classes.userName}>
              {version.creatorName}
              <VersionHistoryOAuthClientAttribution
                oauth2ClientId={version.oauth2ClientId ?? undefined}
                oauth2ClientName={version.oauth2ClientName ?? undefined}
              />
            </span>
          </div>
        )}
      </UnstyledButton>
      <div className={classes.rowAction}>
        {isSelected && (onDownload || onRevert) && (
          <Menu position="bottom-end" withinPortal={withinPortal}>
            <Menu.Target>
              <ActionIcon
                size="sm"
                variant="subtle"
                aria-label="Version actions"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
                {...getTestProps({component: 'SkillVersionHistoryRow', element: 'actionButton'})}
              >
                <BenchlingIcon icon="ellipsis-vertical" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onDownload && (
                <Menu.Item
                  icon={<BenchlingIcon icon="download" />}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onDownload();
                  }}
                  {...getTestProps({
                    component: 'SkillVersionHistoryRow',
                    element: 'downloadButton',
                  })}
                >
                  Download version
                </Menu.Item>
              )}
              {onRevert && (
                <Menu.Item
                  icon={<BenchlingIcon icon="arrow-rotate-left" />}
                  disabled={isReverting}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onRevert();
                  }}
                  {...getTestProps({component: 'SkillVersionHistoryRow', element: 'revertButton'})}
                >
                  Reset to version
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
    </div>
  );
}

export default function SkillVersionHistoryPanel({
  skill,
  onClose,
  onReverted,
  onVersionSelected,
  selectedVersionId,
}: SkillVersionHistoryPanelProps): JSX.Element {
  const {classes} = useStyles();
  const isInChatSettingsModal = useSkillOverflowMenuHandlers() != null;
  const [isReverting, setIsReverting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  const queryResult = useSkippableBenchlingQuery<AgentSkillVersionsQueryData, {id: string}>(
    AGENT_SKILL_VERSIONS_QUERY,
    {
      variables: {id: skill.id},
      fetchPolicy: 'network-only',
    }
  );

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const error = queryResult.type === QueryResultType.ERROR ? 'Failed to load version history.' : revertError;

  const agentSkillResult: AgentSkillVersionsQueryResult | null =
    queryResult.type === QueryResultType.SUCCESS ? queryResult.data.agentSkill : null;
  const isInaccessible = agentSkillResult?.__typename === 'InaccessibleInfoNode';

  const versions: Array<SkillVersion> = useMemo(() => {
    if (agentSkillResult == null || agentSkillResult.__typename !== 'AgentSkillNode') {
      return [];
    }
    return agentSkillResult.versions;
  }, [agentSkillResult]);

  const effectiveSelectedId = selectedVersionId ?? versions.find((v) => v.isCurrent)?.id ?? null;

  const handleSelectVersion = useCallback(
    (version: SkillVersion) => {
      onVersionSelected(version.isCurrent ? null : version);
    },
    [onVersionSelected]
  );

  const handleRevert = useCallback(async () => {
    if (!effectiveSelectedId) {
      return;
    }
    const version = versions.find((v) => v.id === effectiveSelectedId);
    if (!version || version.isCurrent) {
      return;
    }
    setIsReverting(true);
    setRevertError(null);
    try {
      await revertSkillToVersion(skill.id, effectiveSelectedId);
      onReverted();
    } catch (err) {
      if (err instanceof Error) {
        setRevertError(`Failed to revert: ${err.message}`);
      } else {
        throw err;
      }
    } finally {
      setIsReverting(false);
    }
  }, [skill.id, effectiveSelectedId, versions, onReverted]);

  const selectedVersion = versions.find((v) => v.id === effectiveSelectedId);
  const handleDownload = useCallback(() => {
    if (selectedVersion == null) {
      return;
    }
    if (selectedVersion.isCurrent) {
      downloadSkillBundle(skill.id);
    } else {
      downloadSkillBundle(skill.id, selectedVersion.id);
    }
  }, [selectedVersion, skill.id]);

  return (
    <div
      className={classes.container}
      {...getTestProps({component: 'SkillVersionHistoryPanel', element: 'root'})}
    >
      <div className={classes.headerContainer}>
        <span className={classes.title}>Version history</span>
        <Tooltip label="Close" position="left">
          <ActionIcon size="sm" variant="subtle" onClick={onClose} aria-label="Close version history">
            <BenchlingIcon icon="xmark" />
          </ActionIcon>
        </Tooltip>
      </div>

      {isLoading && (
        <Stack align="center" mt="md">
          <Loader size="sm" />
        </Stack>
      )}

      {error && (
        <Box px="md">
          <Text size="sm" c="red">
            {error}
          </Text>
        </Box>
      )}

      {!isLoading && !error && isInaccessible && (
        <Box px="md">
          <Text size="sm" c="dimmed">
            You don't have access to view this skill's version history.
          </Text>
        </Box>
      )}

      {!isLoading && !error && !isInaccessible && versions.length === 0 && (
        <Box px="md">
          <Text size="sm" c="dimmed">
            No versions found.
          </Text>
        </Box>
      )}

      {!isLoading && !error && !isInaccessible && versions.length > 0 && (
        <div className={classes.versionsList}>
          {versions.map((version) => {
            const isSelectedVersion = version.id === effectiveSelectedId;
            return (
              <SkillVersionHistoryRow
                key={version.id}
                version={version}
                isSelected={isSelectedVersion}
                onClick={() => {
                  handleSelectVersion(version);
                }}
                onDownload={isSelectedVersion ? handleDownload : undefined}
                onRevert={isSelectedVersion && !version.isCurrent ? handleRevert : undefined}
                isReverting={isReverting}
                withinPortal={!isInChatSettingsModal}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
